import {
  lazy,
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useUserDataFlattened } from "../../../modules/user/stores";
import { useQueuedPopup } from "../../../hooks/useQueuedPopup";
import { POPUP_CONFIGS } from "@/config/popupConfig";
import { ROUTES } from "@/types/routes";
import {
  useActivityPopupPriority,
  useBetTaskActivity,
  useIsHomeDisplayReady,
  useActivities,
  isActivityIdValid,
} from "@/modules/homeDisplay/hooks";
import { ActivityType, TimingType } from "@/modules/homeDisplay/types";
// 关键：直接从无 .scss 副作用的 helpers/utils 子文件 import，
// 避免通过 components/BetActivity (index.ts) 触发 BetActivityContent.tsx /
// BetActivity.tsx 等带 SCSS side-effect 的组件文件被合并进 main chunk。
// 详见 BetActivity/index.ts 顶部注释。
import {
  shouldShowBetActivity,
  shouldShowBetActivityBadge,
  markBetActivityClickedToday,
  hasClaimableRewards,
  getDay0ClaimableCount,
} from "../components/BetActivity/BetActivity.helpers";
import {
  getDoubleBetEndTimeFromCache, // 用于监听倒计时结束
  getBetActivityEndTimeFromCache, // 用于监听倒计时结束
  safeRefreshUserInfo,
  hasAutoShownDoubleBetToday,
  markDoubleBetAutoShownToday,
  hasClaimableDay1Rewards,
  getLastTriggeredBetAmount,
  setLastTriggeredBetAmount,
  getCurrentMaxClaimableBetAmount,
} from "../components/BetActivity/BetActivity.utils";
import type { BetActivityPopupResult } from "./useBetActivityPopup.types";

const BetActivity = lazy(() =>
  import("../components/BetActivity/BetActivity").then(m => ({
    default: m.BetActivity,
  }))
);

const BET_ACTIVITY_CONFIG = POPUP_CONFIGS.BET_ACTIVITY;

export const useBetActivityPopup = (): BetActivityPopupResult => {
  const {
    data: userInfo,
    isAuthenticated,
    loadUserInfo,
  } = useUserDataFlattened();
  const { list: dailyBetTasks, taskFinished } = useBetTaskActivity();
  const dailyBetTask = useMemo(() => dailyBetTasks[0], [dailyBetTasks]);
  const isConfigReady = useIsHomeDisplayReady();
  const activities = useActivities();

  // 获取活动配置中的 priority，不存在则使用默认值
  const priority = useActivityPopupPriority(
    ActivityType.BET_TASK,
    BET_ACTIVITY_CONFIG.priority,
    TimingType.ACTIVITY_QUALIFICATION
  );

  // 检查活动是否真实存在于 activities 配置中
  const isActivityValid = useMemo(() => {
    return isActivityIdValid(
      dailyBetTask?.taskGroupId,
      ActivityType.BET_TASK,
      activities
    );
  }, [dailyBetTask?.taskGroupId, activities]);

  const isAutoTriggerRef = useRef(false);
  const hasTriggeredDoubleBetRef = useRef(false);

  const [showBetActivityEndTime, setShowBetActivityEndTime] = useState(true);

  const resetAutoTrigger = useCallback(() => {
    isAutoTriggerRef.current = false;
  }, []);

  const BetActivityPopup: React.FC<{
    onComplete: () => void;
  }> = ({ onComplete }) => {
    const [visible, setVisible] = useState(true);

    const handleClose = () => {
      setVisible(false);
      if (isAutoTriggerRef.current) {
        resetAutoTrigger();
      }
      onComplete();
    };

    return (
      <Suspense fallback={null}>
        <BetActivity
          visible={visible}
          onClose={handleClose}
          isAutoTrigger={isAutoTriggerRef.current}
        />
      </Suspense>
    );
  };

  const isDoubleBetActive = dailyBetTask?.betData?.balanceWarning === true;

  const { trigger } = useQueuedPopup({
    popupId: BET_ACTIVITY_CONFIG.id,
    priority,
    shouldShow: () =>
      isConfigReady &&
      isAuthenticated &&
      shouldShowBetActivity(dailyBetTask) &&
      isActivityValid,
    renderContent: onComplete => <BetActivityPopup onComplete={onComplete} />,
    showOnce: false,
    autoTrigger: false,
    allowManualTrigger: true,
    allowedPaths: [ROUTES.HOME, ROUTES.HOME_ALT, "/", ROUTES.LOGIN, ROUTES.REGISTER],
    dependencies: [
      isConfigReady,
      userInfo?.dailyRechargeTasks,
      isActivityValid,
    ],
    userBinding: true,
    getUserId: () => userInfo?.userId,
    timingType: TimingType.ACTIVITY_QUALIFICATION,
  });

  // 监听 bet 加速倒计时结束，结束时安全刷新用户数据
  useEffect(() => {
    if (!isDoubleBetActive) {
      return;
    }

    const doubleBetEndTime = getDoubleBetEndTimeFromCache();

    if (!doubleBetEndTime) {
      return;
    }

    const now = Date.now();
    if (now >= doubleBetEndTime) {
      safeRefreshUserInfo(loadUserInfo);
      return;
    }

    const remainingTime = doubleBetEndTime - now;
    const timer = setTimeout(() => {
      safeRefreshUserInfo(loadUserInfo);
    }, remainingTime);

    return () => {
      clearTimeout(timer);
    };
  }, [
    isDoubleBetActive,
    dailyBetTask?.betData?.balanceWarningExpireSeconds,
    loadUserInfo,
  ]);

  // 监听普通 bet 活动倒计时结束，结束时安全刷新用户数据
  useEffect(() => {
    const betActivityEndTime = getBetActivityEndTimeFromCache();
    setShowBetActivityEndTime(true);
    if (!betActivityEndTime) {
      setShowBetActivityEndTime(false);
      return;
    }

    // 检查是否已过期
    const now = Date.now();
    if (now >= betActivityEndTime) {
      safeRefreshUserInfo(loadUserInfo);
      setShowBetActivityEndTime(false);
      return;
    }

    // day1 可领取奖励，不显示倒计时
    const claimableDay1Rewards = hasClaimableDay1Rewards(dailyBetTask);
    if (claimableDay1Rewards) {
      setShowBetActivityEndTime(false);
      return;
    }

    // taskFinished 为 true，不显示倒计时
    if (taskFinished) {
      setShowBetActivityEndTime(false);
      return;
    }

    // 设置定时器，在倒计时结束时刷新数据,不显示倒计时
    const remainingTime = betActivityEndTime - now;
    const timer = setTimeout(() => {
      safeRefreshUserInfo(loadUserInfo);
      setShowBetActivityEndTime(false);
    }, remainingTime);

    return () => {
      clearTimeout(timer);
    };
  }, [
    dailyBetTask?.betData?.countdownSeconds,
    taskFinished,
    loadUserInfo,
    hasClaimableDay1Rewards,
  ]);

  // 当天第一次出现 bet 加速状态时，自动弹出弹窗
  // ✅ 统一处理 bet 活动弹窗的自动触发逻辑
  const hasTriggeredBetActivityRef = useRef(false);
  useEffect(() => {
    // 如果已经触发过，直接返回（防止同一次渲染周期内重复触发）
    if (hasTriggeredBetActivityRef.current) {
      return;
    }

    if (!isAuthenticated || !shouldShowBetActivity(dailyBetTask)) {
      return;
    }

    // 优先级1：检查是否为当天第一次 bet 加速（触发小火箭动画）
    if (isDoubleBetActive && !hasAutoShownDoubleBetToday(userInfo?.userId)) {
      // 立即标记为已触发，防止重复执行
      hasTriggeredBetActivityRef.current = true;
      hasTriggeredDoubleBetRef.current = true;
      markDoubleBetAutoShownToday(userInfo?.userId);

      // 设置为自动触发（显示小火箭动画）
      isAutoTriggerRef.current = true;
      trigger();
      return;
    }

    // 优先级2：检查是否有新档位可领取（触发普通弹窗）
    if (hasClaimableRewards(dailyBetTask, true)) {
      // 获取当前满足的最高档位金额（有可领取奖励的）
      const currentMaxBetAmount = getCurrentMaxClaimableBetAmount(dailyBetTask);

      // 获取上次触发时的档位金额
      const lastTriggeredAmount = getLastTriggeredBetAmount(
        userInfo?.userId,
        dailyBetTask?.id
      );

      // 只有当前档位 > 上次记录的档位时，才弹出
      if (currentMaxBetAmount > lastTriggeredAmount) {
        // 立即标记为已触发，防止重复执行
        hasTriggeredBetActivityRef.current = true;

        // 记录本次触发的档位金额
        setLastTriggeredBetAmount(
          userInfo?.userId,
          dailyBetTask?.id,
          currentMaxBetAmount
        );

        // ✅ 如果刚刚触发过 double 首次弹出（hasTriggeredDoubleBetRef = true），则保持 isAutoTriggerRef 不变
        // 否则设置为手动触发（不显示火箭动画）
        if (!hasTriggeredDoubleBetRef.current) {
          isAutoTriggerRef.current = false;
        }
        trigger();
      }
    }
  }, [
    isAuthenticated,
    isDoubleBetActive,
    dailyBetTask?.betData?.currentBetAmount,
    userInfo?.userId,
    trigger,
    dailyBetTask,
  ]);

  const doubleMultiplier = dailyBetTask?.betData?.betMultiplier || 2.5;
  const day0ClaimableCount = getDay0ClaimableCount(dailyBetTask);

  // 计算当前最高可领取档位，用于监听变化
  const currentMaxClaimableAmount = useMemo(() => {
    if (!hasClaimableRewards(dailyBetTask, true)) return 0;
    return getCurrentMaxClaimableBetAmount(dailyBetTask);
  }, [dailyBetTask]);

  // 当数据变化时重置标记，允许再次检测
  // 监听任务ID、当前投注金额、最高可领取档位的变化
  useEffect(() => {
    hasTriggeredBetActivityRef.current = false;
  }, [
    dailyBetTask?.id,
    dailyBetTask?.betData?.currentBetAmount,
    currentMaxClaimableAmount,
  ]);

  // 点击 Bet Activity 图标的处理
  const handleBetActivityClick = useCallback(() => {
    // 标记当天已点击
    markBetActivityClickedToday(userInfo?.userId, dailyBetTask?.id ?? "");
    // 设置为手动触发（不显示火箭动画，显示右下角标识）
    isAutoTriggerRef.current = false;
    // 打开 bet activity 弹窗
    trigger();
  }, [trigger, userInfo?.userId, dailyBetTask?.id]);

  const shouldShow = shouldShowBetActivity(dailyBetTask);
  const shouldShowBadge = shouldShowBetActivityBadge(
    dailyBetTask,
    userInfo?.userId
  );

  // ✅ 使用缓存的结束时间戳计算 remainingMs，与弹窗倒计时保持一致
  const remainingMs = useMemo(() => {
    const endTime = isDoubleBetActive
      ? getDoubleBetEndTimeFromCache()
      : getBetActivityEndTimeFromCache();

    if (!endTime) return 0;

    const remaining = endTime - Date.now();
    return Math.max(0, remaining);
  }, [
    isDoubleBetActive,
    dailyBetTask?.betData?.balanceWarningExpireSeconds,
    dailyBetTask?.betData?.countdownSeconds,
  ]);

  return {
    shouldShow,
    trigger,
    remainingMs,
    shouldShowBadge,
    isDoubleBetActive,
    doubleMultiplier,
    handleBetActivityClick,
    showCountdown: showBetActivityEndTime,
    day0ClaimableCount,
    betTask: dailyBetTask,
  };
};
